/**
 * Real, self-contained mobile+password authentication provider for
 * Super Admin, backed by the isolated Super Admin auth backend.
 *
 * Purpose:
 * Keep Super Admin's auth session isolated from Telegram/core-auth
 * identity (there is no shared Identity Service across apps yet) —
 * same isolation rationale as before, now with a real backend behind
 * it instead of a hardcoded credential check.
 *
 * This provider is intentionally isolated so that a future shared
 * Identity API can replace login()/logout() without requiring changes
 * throughout the application — nothing outside this file (or
 * authAdapter.ts, its only caller) knows how auth is actually done.
 *
 * IMPORTANT — circular import avoidance:
 * This file deliberately does NOT import the app's shared `apiClient`
 * singleton from app/api/apiClient.ts. That singleton reads the
 * session via authAdapter.ts, which wraps this file — importing it
 * here would create apiClient -> authAdapter -> localAuthProvider ->
 * apiClient. Instead, a separate, local instance of the same
 * `ApiClient` class from `@vanialabs/core-api` is constructed below,
 * scoped only to the two auth endpoints this file calls. This is not
 * a duplicate of core-api — it's a second configuration of the same
 * stateless, config-driven class, exactly as `ApiClientConfig` is
 * designed to support.
 */

import { ApiClient, ApiError } from '@vanialabs/core-api';
import type { AuthUser } from '../types';

export type { AuthUser };

export interface AuthSession {
  user: AuthUser;
  accessToken: string;
  activeContextId: string;
}

export interface LoginResult {
  ok: boolean;
  session?: AuthSession;
  error?: string;
}

const STORAGE_KEY = 'vania.super-admin.session';
const GENERIC_LOGIN_ERROR = 'خطا در ورود. لطفاً دوباره تلاش کنید.';

// Separate instance from the app's shared apiClient — see file-level
// doc comment above for why. No getAuthToken configured: the login
// call is unauthenticated (skipAuth) and the session call right after
// it uses the just-issued token explicitly (see login() below), since
// that token isn't persisted yet when the session call is made.
const authClient = new ApiClient({
  baseUrl:
    import.meta.env.VITE_API_BASE_URL ??
    'http://localhost:8000',
});

interface SuperAdminAuthLoginResponse {
  access_token: string;
  token_type: string;
  expires_at: string;
  representative: {
    id: number;
    mobile: string;
    full_name: string | null;
    avatar_url: string | null;
  };
  roles: Array<{ id: number; name: string; slug: string; description: string | null }>;
}

interface SuperAdminAuthSessionResponse {
  representative: {
    id: number;
    mobile: string;
    full_name: string | null;
    avatar_url: string | null;
    is_super_admin: boolean;
  } | null;
  roles: Array<{ id: number; name: string; slug: string; description: string | null }>;
}

let cachedSession: AuthSession | null = null;
let hydrated = false;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Reads persisted state once per page load.
 * localStorage is synchronous, so there is no async hydration state.
 */
function hydrate(): void {
  if (hydrated) {
    return;
  }

  hydrated = true;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    cachedSession = raw
      ? (JSON.parse(raw) as AuthSession)
      : null;
  } catch {
    cachedSession = null;
  }
}

function persist(session: AuthSession | null): void {
  try {
    if (session) {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(session),
      );
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Session remains available in module memory for this tab.
  }
}

/** Extracts the backend's own Persian error message when available, so
 * real 401/403/423 responses reach the user unmodified instead of
 * being rewritten here. Falls back to a generic message otherwise. */
function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | undefined;
    if (body && typeof body.detail === 'string') {
      return body.detail;
    }
  }
  return GENERIC_LOGIN_ERROR;
}

/**
 * Real Super Admin login without tenant-host resolution:
 * 1. POST /api/super-admin/auth/login with { mobile, password }.
 * 2. Immediately GET /api/super-admin/auth/session with the returned token.
 */
export async function login(
  mobile: string,
  password: string,
): Promise<LoginResult> {
  let loginData: SuperAdminAuthLoginResponse;

  try {
    const response = await authClient.post<SuperAdminAuthLoginResponse>(
      '/api/super-admin/auth/login',
      { mobile, password },
      { skipAuth: true },
    );
    loginData = response.data;
  } catch (err) {
    return {
      ok: false,
      error: extractErrorMessage(err),
    };
  }

  let sessionData: SuperAdminAuthSessionResponse;

  try {
    const response = await authClient.get<SuperAdminAuthSessionResponse>(
      '/api/super-admin/auth/session',
      { headers: { Authorization: `Bearer ${loginData.access_token}` } },
    );
    sessionData = response.data;
  } catch (err) {
    return {
      ok: false,
      error: extractErrorMessage(err),
    };
  }

  if (!sessionData.representative) {
    return {
      ok: false,
      error: GENERIC_LOGIN_ERROR,
    };
  }

  const session: AuthSession = {
    user: {
      mobile: sessionData.representative.mobile,
      isSuperAdmin: sessionData.representative.is_super_admin,
      // exactOptionalPropertyTypes: `fullName`/`avatarUrl` must be
      // present-and-string or absent entirely — never
      // present-and-undefined — so these are conditional spreads
      // rather than `fullName: ... ?? undefined`.
      ...(sessionData.representative.full_name != null && {
        fullName: sessionData.representative.full_name,
      }),
      ...(sessionData.representative.avatar_url != null && {
        avatarUrl: sessionData.representative.avatar_url,
      }),
    },
    accessToken: loginData.access_token,
    activeContextId: 'super-admin',
  };

  hydrated = true;
  cachedSession = session;
  persist(session);
  notify();

  return {
    ok: true,
    session,
  };
}

export function logout(): void {
  hydrated = true;
  cachedSession = null;
  persist(null);
  notify();
}

export function getSession(): AuthSession | null {
  hydrate();
  return cachedSession;
}

export function isAuthenticated(): boolean {
  return getSession() !== null;
}

/**
 * Subscribes to session changes.
 * Used by authAdapter.ts through useSyncExternalStore.
 */
export function subscribe(
  listener: () => void,
): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}
